import { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

export interface JobTasksRepository {
  listTasks(leadId: string): Promise<any[] | null>;
  updateTask(taskId: string, updates: Record<string, unknown>): Promise<void>;
  createTask(task: Record<string, unknown>): Promise<any | null>;
}

export const supabaseJobTasksRepository: JobTasksRepository = {
  async listTasks(leadId) {
    const { data, error } = await supabase.from('tasks').select('*').eq('lead_id', leadId).order('created_at', { ascending: false });
    return error ? null : data;
  },
  async updateTask(taskId, updates) {
    const { error } = await supabase.from('tasks').update(updates).eq('id', taskId);
    if (error) throw error;
  },
  async createTask(task) {
    const { data, error } = await supabase.from('tasks').insert([task]).select().single();
    return error ? null : data;
  },
};

export const useJobTasks = (
  isOpen: boolean,
  dataId?: string,
  expandedInquiry?: string | null,
  repository: JobTasksRepository = supabaseJobTasksRepository,
  sharedTasks?:any[],
) => {
  const [jobTasks, setJobTasks] = useState<any[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  useEffect(() => {
    const fetchTasks = async () => {
      const targetLeadId = expandedInquiry || dataId;
      if (!targetLeadId) return;
      const tasks = await repository.listTasks(targetLeadId);
      if (tasks) setJobTasks(tasks);
    };
    if (isOpen && !sharedTasks) fetchTasks();
  }, [dataId, expandedInquiry, isOpen, repository, sharedTasks]);

  const toggleTask = async (task: any) => {
    const status = task.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
    setJobTasks(previous => previous.map(item => item.id === task.id ? { ...item, status } : item));
    try {
      await repository.updateTask(task.id, { status });
    } catch (error: any) {
      setJobTasks(previous => previous.map(item => item.id === task.id ? { ...item, status: task.status } : item));
      window.alert('Failed to update task: ' + error.message);
    }
  };

  const updateTaskDate = async (taskId: string, date: string) => {
    if (!date) return;
    const due_date = new Date(date).toISOString();
    const previousTask = jobTasks.find(item => item.id === taskId);
    setJobTasks(previous => previous.map(item => item.id === taskId ? { ...item, due_date } : item));
    try {
      await repository.updateTask(taskId, { due_date });
    } catch (error: any) {
      setJobTasks(previous => previous.map(item => item.id === taskId ? { ...item, due_date: previousTask?.due_date } : item));
      window.alert('Failed to update task date: ' + error.message);
    }
  };

  const addTask = async () => {
    const lead_id = expandedInquiry || dataId;
    if (!newTaskTitle.trim() || !lead_id) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newTask = {
      title: newTaskTitle.trim(),
      lead_id,
      task_type: 'MANUAL',
      status: 'PENDING',
      due_date: tomorrow.toISOString(),
    };
    const data = await repository.createTask(newTask);
    if (data) {
      setJobTasks(previous => [data, ...previous]);
      setNewTaskTitle('');
    }
  };

  return {
    jobTasks: sharedTasks?sharedTasks.filter(t=>t.lead_id===(expandedInquiry||dataId)):jobTasks,
    setJobTasks,
    newTaskTitle,
    setNewTaskTitle,
    toggleTask,
    updateTaskDate,
    addTask,
  };
};
